import React, { useState } from 'react';

// KIOSK MATERIAL REQUEST WORKFLOW
const API = '/api/kiosk';

const newRequestKey = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  const values = new Uint8Array(16);
  crypto.getRandomValues(values);

  values[6] = (values[6] & 15) | 64;
  values[8] = (values[8] & 63) | 128;

  const hex = [...values]
    .map(value =>
      value.toString(16).padStart(2, '0')
    )
    .join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20)
  ].join('-');
};

const newLine = () => ({
  material_id:'',
  requested_quantity:'',
  notes:''
});

export default function KioskMaterialRequest({
  task,
  user
}) {
  const [open, setOpen] = useState(false);
  const [verified, setVerified] = useState(false);
  const [pin, setPin] = useState('');
  const [materials, setMaterials] = useState([]);
  const [history, setHistory] = useState([]);
  const [purpose, setPurpose] = useState('');
  const [requiredDate, setRequiredDate] =
    useState('');
  const [lines, setLines] = useState([
    newLine()
  ]);
  const [requestKey, setRequestKey] =
    useState(newRequestKey);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] =
    useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const close = () => {
    setOpen(false);
    setVerified(false);
    setPin('');
    setMaterials([]);
    setHistory([]);
    setPurpose('');
    setRequiredDate('');
    setLines([newLine()]);
    setRequestKey(newRequestKey());
    setLoading(false);
    setSubmitted(false);
    setError('');
    setMessage('');
  };

  const pressPin = value => {
    if (loading) return;

    if (value === '⌫') {
      setPin(current =>
        current.slice(0, -1)
      );
      return;
    }

    if (pin.length < 4) {
      setPin(current =>
        current + String(value)
      );
    }
  };

  const verifyAndLoad = async () => {
    if (!user?.id || pin.length !== 4) {
      setError(
        'Enter your own 4-digit kiosk PIN'
      );
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(
        `${API}/tasks/${task.id}/material-request-options`,
        {
          method:'POST',
          headers:{
            'Content-Type':'application/json'
          },
          body:JSON.stringify({
            user_id:user.id,
            pin
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(
          data.error ||
          'Material choices could not be loaded'
        );
        setPin('');
        return;
      }

      setMaterials(
        data.data?.materials || []
      );
      setHistory(
        data.data?.previous_requests || []
      );
      setVerified(true);
    } catch (_) {
      setError(
        'Network error. Material choices could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  const updateLine = (
    index,
    field,
    value
  ) => {
    setLines(current =>
      current.map((line, lineIndex) =>
        lineIndex === index
          ? {...line, [field]:value}
          : line
      )
    );
  };

  const removeLine = index => {
    setLines(current => {
      const remaining = current.filter(
        (_, lineIndex) =>
          lineIndex !== index
      );

      return remaining.length
        ? remaining
        : [newLine()];
    });
  };

  const submit = async () => {
    setError('');
    setMessage('');

    if (!purpose.trim()) {
      setError(
        'Enter why the materials are required'
      );
      return;
    }

    const preparedLines = lines
      .filter(line =>
        line.material_id ||
        line.requested_quantity ||
        line.notes
      )
      .map(line => ({
        material_id:materials.find(m => (m.sku_id || m.material_id) === line.material_id)?.material_id,
        sku_id:materials.find(m => (m.sku_id || m.material_id) === line.material_id)?.sku_id || null,
        requested_quantity:
          Number(line.requested_quantity),
        notes:line.notes.trim()
      }));

    if (!preparedLines.length) {
      setError(
        'Add at least one material'
      );
      return;
    }

    if (
      preparedLines.some(line =>
        !line.material_id ||
        !Number.isFinite(
          line.requested_quantity
        ) ||
        line.requested_quantity <= 0
      )
    ) {
      setError(
        'Select every material and enter a quantity greater than zero'
      );
      return;
    }

    const materialIds = preparedLines.map(
      line => line.sku_id || line.material_id
    );

    if (
      new Set(materialIds).size !==
      materialIds.length
    ) {
      setError(
        'The same material cannot be requested twice'
      );
      return;
    }

    if (pin.length !== 4) {
      setVerified(false);
      setError(
        'Re-enter your own 4-digit kiosk PIN'
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API}/tasks/${task.id}/material-requests`,
        {
          method:'POST',
          headers:{
            'Content-Type':'application/json'
          },
          body:JSON.stringify({
            user_id:user.id,
            pin,
            idempotency_key:requestKey,
            purpose:purpose.trim(),
            required_date:
              requiredDate || null,
            lines:preparedLines
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(
          data.error ||
          'Material request could not be submitted'
        );

        if (
          data.code ===
          'KIOSK_PIN_INVALID'
        ) {
          setVerified(false);
          setPin('');
        }

        return;
      }

      const number =
        data.data?.requisition_number;

      setMessage(
        data.message ||
        (
          number
            ? `${number} was submitted to Inventory.`
            : 'Material request was submitted to Inventory.'
        )
      );

      setHistory(current => [
        {
          requisition_number:
            number || 'Submitted request',
          status:
            data.data?.status ||
            'submitted',
          purpose:purpose.trim(),
          required_date:
            requiredDate || null,
          created_at:
            new Date().toISOString()
        },
        ...current
      ]);

      setSubmitted(true);
      setPurpose('');
      setRequiredDate('');
      setLines([newLine()]);
      setPin('');
      setRequestKey(newRequestKey());
    } catch (_) {
      setError(
        'Network error. Press Submit again; duplicate protection is active.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          width:'100%',
          marginTop:10,
          padding:'10px 12px',
          borderRadius:8,
          border:
            '1px solid rgba(168,137,61,.75)',
          background:
            'rgba(168,137,61,.16)',
          color:'#F2EFE7',
          fontWeight:700,
          cursor:'pointer'
        }}
      >
        Request Materials
      </button>
    );
  }

  return (
    <div style={{
      marginTop:12,
      padding:14,
      borderRadius:12,
      background:'rgba(0,0,0,.24)',
      border:
        '1px solid rgba(168,137,61,.55)'
    }}>
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'flex-start',
        gap:10,
        marginBottom:12
      }}>
        <div>
          <div style={{
            color:'#F2EFE7',
            fontWeight:700,
            fontSize:13
          }}>
            Request Materials
          </div>

          <div style={{
            color:'rgba(242,239,231,.6)',
            fontSize:10,
            marginTop:3
          }}>
            This request will be linked to
            your Production Task and Job Order.
          </div>
        </div>

        <button
          onClick={close}
          disabled={loading}
          aria-label="Close material request"
          style={{
            border:'none',
            background:'transparent',
            color:'#F2EFE7',
            fontSize:20,
            cursor:'pointer',
            padding:0
          }}
        >
          ×
        </button>
      </div>

      {error && (
        <div style={{
          background:'rgba(143,46,36,.28)',
          border:
            '1px solid rgba(255,170,170,.45)',
          color:'#FFD5D0',
          borderRadius:8,
          padding:9,
          marginBottom:10,
          fontSize:11
        }}>
          {error}
        </div>
      )}

      {message && (
        <div style={{
          background:'rgba(85,107,47,.35)',
          border:
            '1px solid rgba(168,137,61,.6)',
          color:'#F2EFE7',
          borderRadius:8,
          padding:10,
          marginBottom:10,
          fontSize:11,
          fontWeight:700
        }}>
          {message}
        </div>
      )}

      {!verified ? (
        <>
          <div style={{
            color:'rgba(242,239,231,.72)',
            fontSize:11,
            textAlign:'center',
            marginBottom:10
          }}>
            Enter your own kiosk PIN to
            request materials for this task.
          </div>

          <div style={{
            display:'flex',
            justifyContent:'center',
            gap:8,
            marginBottom:12
          }}>
            {[0,1,2,3].map(index => (
              <div
                key={index}
                style={{
                  width:12,
                  height:12,
                  borderRadius:'50%',
                  background:
                    pin.length > index
                      ? '#A8893D'
                      : 'rgba(242,239,231,.18)',
                  border:
                    '2px solid rgba(168,137,61,.5)'
                }}
              />
            ))}
          </div>

          <div style={{
            display:'grid',
            gridTemplateColumns:
              'repeat(3,1fr)',
            gap:8,
            maxWidth:220,
            margin:'0 auto'
          }}>
            {[
              1,2,3,4,5,6,7,8,9,'',0,'⌫'
            ].map((key, index) => (
              <button
                key={index}
                disabled={
                  key === '' || loading
                }
                onClick={() =>
                  key !== '' &&
                  pressPin(key)
                }
                style={{
                  padding:13,
                  borderRadius:9,
                  border:'none',
                  fontSize:18,
                  fontWeight:600,
                  cursor:
                    key === ''
                      ? 'default'
                      : 'pointer',
                  background:
                    key === ''
                      ? 'transparent'
                      : 'rgba(242,239,231,.1)',
                  color:'#F2EFE7',
                  opacity:
                    key === '' ? 0 : 1
                }}
              >
                {key}
              </button>
            ))}
          </div>

          <button
            onClick={verifyAndLoad}
            disabled={
              pin.length !== 4 ||
              loading
            }
            style={{
              width:'100%',
              marginTop:12,
              padding:'11px 12px',
              borderRadius:8,
              border:'none',
              background:
                pin.length === 4
                  ? '#A8893D'
                  : 'rgba(242,239,231,.12)',
              color:
                pin.length === 4
                  ? '#3B2A21'
                  : 'rgba(242,239,231,.5)',
              fontWeight:700,
              cursor:'pointer'
            }}
          >
            {loading
              ? 'Checking...'
              : 'Open Material Request'}
          </button>
        </>
      ) : (
        <>
          <div style={{
            background:'rgba(242,239,231,.08)',
            borderRadius:8,
            padding:10,
            marginBottom:12,
            color:'rgba(242,239,231,.72)',
            fontSize:10
          }}>
            <strong style={{
              color:'#F2EFE7'
            }}>
              {task.job_number ||
                'Production Job'}
            </strong>
            <br />
            Request only what is required
            for your assigned work. Inventory
            will check availability before issue.
          </div>

          <label style={{
            display:'block',
            color:'rgba(242,239,231,.72)',
            fontSize:10,
            marginBottom:4
          }}>
            Reason for request *
          </label>

          <textarea
            value={purpose}
            onChange={event =>
              setPurpose(event.target.value)
            }
            disabled={loading || submitted}
            rows={2}
            maxLength={2000}
            placeholder={
              `Materials required to complete ${task.title}`
            }
            style={{
              width:'100%',
              boxSizing:'border-box',
              padding:9,
              borderRadius:7,
              border:'1px solid #ddd',
              marginBottom:9,
              fontSize:11
            }}
          />

          <label style={{
            display:'block',
            color:'rgba(242,239,231,.72)',
            fontSize:10,
            marginBottom:4
          }}>
            Date required
          </label>

          <input
            type="date"
            value={requiredDate}
            onChange={event =>
              setRequiredDate(
                event.target.value
              )
            }
            disabled={loading || submitted}
            style={{
              width:'100%',
              boxSizing:'border-box',
              padding:9,
              borderRadius:7,
              border:'1px solid #ddd',
              marginBottom:10,
              fontSize:11
            }}
          />

          {lines.map((line, index) => {
            const chosen = materials.find(
              material =>
                material.id ===
                line.material_id
            );

            return (
              <div
                key={index}
                style={{
                  background:
                    'rgba(242,239,231,.08)',
                  borderRadius:8,
                  padding:9,
                  marginBottom:8
                }}
              >
                <div style={{
                  display:'flex',
                  justifyContent:
                    'space-between',
                  marginBottom:6
                }}>
                  <span style={{
                    color:'#F2EFE7',
                    fontSize:10,
                    fontWeight:700
                  }}>
                    Material {index + 1}
                  </span>

                  {lines.length > 1 && (
                    <button
                      onClick={() =>
                        removeLine(index)
                      }
                      disabled={
                        loading || submitted
                      }
                      style={{
                        border:'none',
                        background:'transparent',
                        color:'#FFB5AE',
                        fontSize:10,
                        cursor:'pointer'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <select
                  value={line.material_id}
                  onChange={event =>
                    updateLine(
                      index,
                      'material_id',
                      event.target.value
                    )
                  }
                  disabled={loading || submitted}
                  style={{
                    width:'100%',
                    boxSizing:'border-box',
                    padding:9,
                    borderRadius:7,
                    border:'1px solid #ddd',
                    marginBottom:7,
                    fontSize:11
                  }}
                >
                  <option value="">
                    Select material
                  </option>

                  {materials.map(material => (
                    <option
                      key={material.id}
                      value={material.id}
                    >
                      {material.name}
                      {material.material_code
                        ? ` (${material.material_code})`
                        : ''}
                      {' — '}
                      {material.unit_of_measure}
                    </option>
                  ))}
                </select>

                <div style={{
                  display:'grid',
                  gridTemplateColumns:'1fr 1fr',
                  gap:7
                }}>
                  <input
                    type="number"
                    min="0.0001"
                    step="any"
                    value={
                      line.requested_quantity
                    }
                    onChange={event =>
                      updateLine(
                        index,
                        'requested_quantity',
                        event.target.value
                      )
                    }
                    disabled={
                      loading || submitted
                    }
                    placeholder={
                      chosen
                        ? `Quantity (${chosen.unit_of_measure})`
                        : 'Quantity'
                    }
                    style={{
                      width:'100%',
                      boxSizing:'border-box',
                      padding:8,
                      borderRadius:7,
                      border:'1px solid #ddd',
                      fontSize:11
                    }}
                  />

                  <input
                    value={line.notes}
                    onChange={event =>
                      updateLine(
                        index,
                        'notes',
                        event.target.value
                      )
                    }
                    disabled={
                      loading || submitted
                    }
                    maxLength={1000}
                    placeholder="Notes"
                    style={{
                      width:'100%',
                      boxSizing:'border-box',
                      padding:8,
                      borderRadius:7,
                      border:'1px solid #ddd',
                      fontSize:11
                    }}
                  />
                </div>
              </div>
            );
          })}

          {!submitted && (
            <>
              <button
                onClick={() =>
                  setLines(current => [
                    ...current,
                    newLine()
                  ])
                }
                disabled={loading}
                style={{
                  width:'100%',
                  padding:9,
                  borderRadius:8,
                  border:
                    '1px dashed rgba(242,239,231,.4)',
                  background:'transparent',
                  color:'#F2EFE7',
                  cursor:'pointer',
                  fontSize:11,
                  marginBottom:10
                }}
              >
                + Add Another Material
              </button>

              <div style={{
                color:'rgba(242,239,231,.68)',
                textAlign:'center',
                fontSize:10,
                marginBottom:8
              }}>
                Your own kiosk PIN confirms
                that this request belongs to you.
              </div>

              <button
                onClick={submit}
                disabled={loading}
                style={{
                  width:'100%',
                  padding:'11px 12px',
                  borderRadius:8,
                  border:'none',
                  background:'#556B2F',
                  color:'#F2EFE7',
                  fontWeight:700,
                  cursor:'pointer'
                }}
              >
                {loading
                  ? 'Submitting...'
                  : 'Submit to Inventory'}
              </button>
            </>
          )}

          {submitted && (
            <button
              onClick={() => {
                setSubmitted(false);
                setMessage('');
                setVerified(false);
                setPin('');
              }}
              style={{
                width:'100%',
                padding:'10px 12px',
                borderRadius:8,
                border:
                  '1px solid rgba(242,239,231,.35)',
                background:'transparent',
                color:'#F2EFE7',
                fontWeight:700,
                cursor:'pointer'
              }}
            >
              Make Another Request
            </button>
          )}

          {history.length > 0 && (
            <div style={{
              marginTop:12,
              paddingTop:10,
              borderTop:
                '1px solid rgba(242,239,231,.16)'
            }}>
              <div style={{
                color:'rgba(242,239,231,.72)',
                fontSize:10,
                fontWeight:700,
                marginBottom:6
              }}>
                Previous Requests for This Task
              </div>

              {history
                .slice(0, 5)
                .map((request, index) => (
                  <div
                    key={
                      request.requisition_number ||
                      index
                    }
                    style={{
                      background:
                        'rgba(242,239,231,.06)',
                      borderRadius:7,
                      padding:8,
                      marginBottom:5,
                      color:
                        'rgba(242,239,231,.68)',
                      fontSize:10
                    }}
                  >
                    <strong style={{
                      color:'#F2EFE7'
                    }}>
                      {request.requisition_number}
                    </strong>
                    {' · '}
                    {String(
                      request.status ||
                      'submitted'
                    ).replace(/_/g, ' ')}

                    {request.purpose && (
                      <div style={{
                        marginTop:3
                      }}>
                        {request.purpose}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
